import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MenuItem, TextField } from "@mui/material";
import CollapsibleSection from "components/Section";
import { Grid } from "components/Grid";
import { BarChart } from "components/Charts/BarChart";
import { LineChart } from "components/Charts/LineChart";
import { PieChart } from "components/Charts/PieChart";
import { TDataTableColumn } from "components/Grid/types";
import {
  getMetersReport,
  METERS_REPORT_REFRESH_INTERVAL_MS,
  TOP_N_OPTIONS,
} from "../api";
import { useReportData } from "../useReportData";
import { useReportFilters } from "../useReportFilters";
import { useGridDetailsContextMenu } from "components/Grid/hooks/useGridDetailsContextMenu";
import { TMeterMapPoint } from "../types";
import { KpiCard } from "../components/KpiCard";
import { ReportSubHeader } from "../components/ReportSubHeader";
import { ReportFiltersBar } from "../components/ReportFiltersBar";
import { ReportBand } from "../components/ReportBand";
import { MetersMap } from "../components/MetersMap";

/** Every grid on this page is capped, so the page scrolls instead of the browser. */
const GRID_HEIGHT = 380;

const topConsumersColumns: TDataTableColumn[] = [
  { dataField: "identifierId", caption: "identifier", dataType: "string" },
  {
    dataField: "consumption",
    caption: "consumptionCubicMeter",
    dataType: "number",
    sortOrder: "desc",
  },
];

const errorMetersColumns: TDataTableColumn[] = [
  { dataField: "identifierId", caption: "identifier", dataType: "string" },
  { dataField: "alarmLabel", caption: "alarmType", dataType: "string" },
];

const MetersReportView = () => {
  const { t } = useTranslation();
  const { filters, applyFilters, isReady } = useReportFilters();
  const {
    data,
    isLoading,
    isRefetching,
    lastUpdated,
    lastAttempt,
    refreshIntervalMs,
    refresh,
  } = useReportData(
    getMetersReport,
    filters,
    isReady,
    METERS_REPORT_REFRESH_INTERVAL_MS
  );
  const meterDetailsContextMenu = useGridDetailsContextMenu((row) =>
    row?.id ? `/dashboard/meters/details/${row.id}` : undefined
  );

  /**
   * Display-only slice size. Local state rather than a URL filter on purpose:
   * the request already returns every meter, so changing the slice must not
   * trigger a refetch (a URL filter change would).
   */
  const [topN, setTopN] = useState<number>(TOP_N_OPTIONS[0]);

  const allMeters = data?.meterUsage || [];
  /** `meterUsage` arrives sorted desc, so the slice is the top-N. */
  const topMeters = useMemo(() => allMeters.slice(0, topN), [allMeters, topN]);

  const topMetersUsage = useMemo(
    () => topMeters.reduce((sum, item) => sum + item.consumption, 0),
    [topMeters]
  );

  const topMetersShare = useMemo(() => {
    if (!data?.consumptionInRange) return undefined;
    return `${((topMetersUsage / data.consumptionInRange) * 100).toFixed(1)}%`;
  }, [data, topMetersUsage]);

  const averageTopMeterUsage = useMemo(() => {
    if (!topMeters.length) return undefined;
    return Number((topMetersUsage / topMeters.length).toFixed(1));
  }, [topMeters, topMetersUsage]);

  const mapPoints = useMemo<TMeterMapPoint[]>(
    () =>
      topMeters.map((item) => ({
        id: item.id,
        identifierId: item.identifierId,
        latitude: item.latitude,
        longitude: item.longitude,
        consumption: item.consumption,
        isTopConsumer: true,
        hasAlarm: false,
      })),
    [topMeters]
  );

  const topNBadge = `${topN.toLocaleString("fa-IR")} ${t("meter")}`;

  return (
    <>
      <ReportSubHeader
        title={t("metersReport")}
        lastUpdated={lastUpdated}
        lastAttempt={lastAttempt}
        refreshIntervalMs={refreshIntervalMs}
        isLoading={isLoading}
        isRefetching={isRefetching}
        onRefresh={refresh}
      />
      <ReportFiltersBar
        filters={filters}
        onApply={applyFilters}
        isLoading={isLoading}
      />
      <div className="flex flex-col gap-4 p-4 h-full overflow-auto [&>*]:shrink-0">
        {/* ============ Band 1 — every meter in the filtered region ============ */}
        <ReportBand
          title={t("allMetersBand")}
          badge={t("allMetersScope")}
          description={t("allMetersBandDesc")}
          tone="neutral"
        />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            title={t("totalMeters")}
            value={data?.totalMeters}
            iconName="MdGasMeter"
            isLoading={isLoading}
          />
          <KpiCard
            title={t("metersWithAlarm")}
            value={data?.metersWithAlarm}
            iconName="MdWarningAmber"
            isLoading={isLoading}
          />
          <KpiCard
            title={t("consumptionToday")}
            value={data?.consumptionToday}
            unit={t("cubicMeter")}
            iconName="MdWaterDrop"
            isLoading={isLoading}
          />
          <KpiCard
            title={t("totalConsumptionInRange")}
            value={data?.consumptionInRange}
            unit={t("cubicMeter")}
            iconName="MdOutlineWaterDrop"
            isLoading={isLoading}
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
          <CollapsibleSection
            title={t("dailyConsumptionTrend")}
            className="xl:col-span-2"
            collapsible={false}
            isLoading={isLoading}
            skeleton="chart"
          >
            {data && (
              <BarChart
                series={[
                  {
                    type: "column",
                    data: data.dailyConsumption.map((d) => d.value),
                    options: { name: t("consumption") },
                  },
                ]}
                xAxis={{
                  categories: data.dailyConsumption.map((d) => d.label),
                }}
                yAxis={{ title: { text: t("cubicMeter") } }}
              />
            )}
          </CollapsibleSection>
          <CollapsibleSection
            title={t("alarmsByType")}
            collapsible={false}
            isLoading={isLoading}
            skeleton="chart"
          >
            {data && (
              <LineChart
                series={[
                  {
                    type: "column",
                    name: t("alarmsCount"),
                    data: data.alarmsByType.map((item) => item.value),
                  },
                ]}
                xAxis={{
                  categories: data.alarmsByType.map((item) => item.name),
                }}
                yAxis={{ title: { text: t("count") } }}
              />
            )}
          </CollapsibleSection>
          <CollapsibleSection
            title={t("meterStatusBreakdown")}
            collapsible={false}
            isLoading={isLoading}
            skeleton="pie"
          >
            {data && (
              <PieChart
                // Labels off: the legend already names the three states, and
                // their leader lines were squeezing the pie in this narrow column.
                dataLabels={false}
                series={[
                  {
                    type: "pie",
                    name: t("count"),
                    data: data.statusBreakdown.map((item) => ({
                      name: item.name,
                      y: item.value,
                    })),
                  },
                ]}
              />
            )}
          </CollapsibleSection>
        </div>

        <CollapsibleSection
          title={t("metersWithAlarm")}
          collapsible={false}
          isLoading={isLoading}
          skeleton="table"
          minHeight={GRID_HEIGHT}
        >
          <Grid
            gridKey="metersReportErrors"
            id="metersReportErrorsGrid"
            gridName={t("metersWithAlarm")}
            dataSource={data?.metersWithErrors || []}
            keyExpr="id"
            columns={errorMetersColumns}
            rowContextMenu={meterDetailsContextMenu}
            height={GRID_HEIGHT}
            hasSearchPanel={false}
            hasPagination={true}
            hasGroupingFeatures={false}
            hasFilterBuilder={false}
            allowedChangeScrolling={true}
          />
        </CollapsibleSection>

        {/* ============== Band 2 — the user's top-N slice of those ============== */}
        <ReportBand
          title={t("topConsumersBand")}
          badge={topNBadge}
          description={t("topConsumersBandDesc")}
          tone="accent"
          action={
            <TextField
              select
              size="small"
              label={t("topNSelectorLabel")}
              value={topN}
              onChange={(event) => setTopN(Number(event.target.value))}
              className="w-[116px] bg-white"
              disabled={isLoading}
            >
              {TOP_N_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option.toLocaleString("fa-IR")}
                </MenuItem>
              ))}
            </TextField>
          }
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <KpiCard
            title={t("topConsumersUsage")}
            value={data ? topMetersUsage : undefined}
            unit={t("cubicMeter")}
            iconName="MdTrendingUp"
            isLoading={isLoading}
          />
          <KpiCard
            title={t("topConsumersShare")}
            value={topMetersShare}
            caption={t("topConsumersShareCaption")}
            hint={t("topConsumersShareHint")}
            iconName="MdOutlinePieChart"
            isLoading={isLoading}
          />
          <KpiCard
            title={t("averageTopConsumerUsage")}
            value={averageTopMeterUsage}
            unit={t("cubicMeter")}
            iconName="MdOutlineAnalytics"
            isLoading={isLoading}
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <CollapsibleSection
            title={t("consumptionPerMeter")}
            className="xl:col-span-2"
            collapsible={false}
            isLoading={isLoading}
            skeleton="chart"
          >
            {data && (
              <LineChart
                series={[
                  {
                    type: "column",
                    name: t("consumption"),
                    data: topMeters.map((item) => item.consumption),
                  },
                ]}
                xAxis={{
                  categories: topMeters.map((item) => item.identifierId),
                }}
                yAxis={{ title: { text: t("cubicMeter") } }}
              />
            )}
          </CollapsibleSection>
          <CollapsibleSection
            title={t("metersList")}
            collapsible={false}
            isLoading={isLoading}
            skeleton="table"
            minHeight={GRID_HEIGHT}
          >
            <Grid
              gridKey="metersReportTopConsumers"
              id="metersReportTopConsumersGrid"
              gridName={t("topConsumers")}
              dataSource={topMeters}
              keyExpr="id"
              columns={topConsumersColumns}
              rowContextMenu={meterDetailsContextMenu}
              height={GRID_HEIGHT}
              hasSearchPanel={false}
              hasPagination={false}
              hasGroupingFeatures={false}
              hasFilterBuilder={false}
              allowedChangeScrolling={true}
            />
          </CollapsibleSection>
        </div>

        <CollapsibleSection
          title={t("metersMap")}
          collapsible={false}
          isLoading={isLoading}
          skeleton="map"
          minHeight={520}
        >
          <MetersMap points={mapPoints} height={480} isLoading={isLoading} />
        </CollapsibleSection>
      </div>
    </>
  );
};

export default MetersReportView;
